import { useEffect, useMemo, useState } from 'react';
import { createElement } from 'react';
import { createBrowserRouter } from 'react-router';
import { getRouter } from '../../getRouter';
import { useMeQuery } from '../../apiHooks/useMeQuery';
import { useAuthStore } from '../../components/Login/useAuthStore';
import { useIsPermittedOrigin } from '../../appState/hooks/useOrigin';
import { ErrorView } from '../../components/ErrorView';
import { getDeactivatedAccessMessage, isDeactivatedAccountError } from '../functional/getDeactivatedAccessMessage';
import { getSessionTenantId } from '../storage/tenantSessionStorage';

export function useRouter() {
  const location = window.location;
  const otpRequested = useAuthStore((state) => state.loginStatus) === 'otpRequested';
  const loginStatus = useAuthStore((state) => state.loginStatus);
  const { setError, clearInvalidSession } = useAuthStore();

  const [origin, setOrigin] = useState(location.origin);
  useEffect(() => {
    setOrigin(location.origin);
  }, [location.origin]);
  const { data: meData, error: meError } = useMeQuery();
  const isPermittedOrigin = useIsPermittedOrigin();
  const deactivatedMessage = getDeactivatedAccessMessage(meError);
  const isDeactivatedAccount = isDeactivatedAccountError(meError);

  useEffect(() => {
    if (!deactivatedMessage) {
      return;
    }
    setError(deactivatedMessage);

    // HRI2-17930: the session that produced this error is unusable, so discard it once the
    // message is on screen - otherwise the persisted Cognito tokens keep restoring it and every
    // other user of this browser sees the same error page.
    //
    // Every 400 from /me shows the message, but only the two codes that name the user itself
    // (user_not_found, user_disabled) may destroy a session. The other 400s the endpoint can
    // answer - invalid_token_claims, toplevel_org_not_found - are tenant configuration faults
    // that would otherwise sign out every user of that tenant at once.
    //
    // The teardown is idempotent and stays in this tab: no redirect, and the error message is
    // left untouched. It is not purely local, though - Amplify's signOut() revokes the refresh
    // token at Cognito when the access token carries an origin_jti, which also invalidates the
    // tokens issued from it in the user's other tabs and sessions.
    if (isDeactivatedAccount) {
      clearInvalidSession();
    }
  }, [clearInvalidSession, deactivatedMessage, isDeactivatedAccount, setError]);

  const router = useMemo(() => {
    if (deactivatedMessage) {
      return createBrowserRouter([
        {
          path: '*',
          element: createElement(ErrorView, {
            error: { message: deactivatedMessage },
            actionLabel: 'Sign in with a different account',
            onTryAgain: signOutAndReturnToLogin,
          }),
        },
      ]);
    }
    return getRouter({
      origin,
      otpRequested,
      isManager: meData ? meData.portalRole === 'manager' : undefined,
      loginStatus,
      isPermittedOrigin,
    });
  }, [origin, otpRequested, meData, loginStatus, isPermittedOrigin, deactivatedMessage]);

  return router;
}

/**
 * Escape hatch from the deactivated-account error page. Goes through the Cognito hosted UI logout
 * so the IdP session ends too - clearing only the app side would let SSO silently sign the same
 * unusable user straight back in instead of prompting for another account.
 */
function signOutAndReturnToLogin() {
  const { config, logout } = useAuthStore.getState();
  logout();
  if (!config) {
    // logout() only redirects when it has a config to build the hosted UI URL from. The stored
    // tenant is absent on origins that serve the app without a :tenantId segment, and an empty
    // one would make `/${tenantId}/login` the protocol-relative URL "//login", i.e. off-origin.
    const tenantId = getSessionTenantId();
    window.location.assign(tenantId && /^[A-Za-z0-9-]{1,64}$/.test(tenantId) ? `/${tenantId}/login` : '/');
  }
}
